/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import type {
  FileEditorDescription,
  InputConfig,
  InputDescription,
} from "@smartsuite/automation-engine-api";
import type { RootStore } from "../stores";
import { rootStore } from "../stores";
import type { CandidateStore } from "../stores/automation/candidate.store";
import {
  candidateActionConditionsValid,
  candidateActionInputReferenceValid,
  candidateActionInputValid,
} from "../stores/automation/utils/inputValidation";
import { recordSourceMissing } from "./record-source";
import { validateAttachmentsInput } from "../stores/automation/utils/validate-attachments";
import type {
  ActionCandidate,
  ActionGroupType,
  AutomationCandidate,
} from "../types/candidate";
import {
  findRecordsActionsToOptions,
  triggerToOption,
} from "../containers/AutomationForm/Forms/InputForm/RecordsSourceEditor/utils";
import { t } from "i18next";
import { InputGroup, ActionLabel } from "./constants";
import { isValueExist } from "../services/isValueExist";
import { OperatorDescriptionKinds } from "../containers/AutomationForm/Forms/InputForm/FieldEditor/FieldEditorRow/containers/UpdateValueDropdown/constanst";

/**
 * Checks if an action is a create-record or update-record action.
 */
export const isRecordAction = (actionId?: string): boolean => {
  return (
    actionId === ActionLabel.createRecord ||
    actionId === ActionLabel.updateRecord
  );
};

/**
 * Checks if an input belongs to the fields group.
 */
export const isInputInFieldsGroup = (
  inputDescription: InputDescription | undefined,
): boolean => {
  return (
    inputDescription?.display?.display.oneofKind === "group" &&
    inputDescription.display.display.group.groupId === InputGroup.fieldsGroup
  );
};

/**
 * Checks if a field input has a value or is configured with "Clear Value" operator.
 * A field is considered configured if:
 * - It has a value (checked via isValueExist), OR
 * - It has a "Clear Value" operator (which is a valid use case for clearing a field)
 */
export const hasFieldValueOrClearOperator = (input: InputConfig): boolean => {
  return (
    isValueExist(input) ||
    input.operatorConfig?.operator.oneofKind === OperatorDescriptionKinds.CLEAR
  );
};

/**
 * Counts how many fields in the fields group have values.
 * Returns the count of fields with values.
 * A field is considered to have a value if:
 * - It has a value (checked via isValueExist), OR
 * - It has a "Clear Value" operator (which is a valid use case for clearing a field)
 */
export const countFieldsGroupWithValues = (
  inputs: InputConfig[],
  inputDescriptions: InputDescription[],
): number => {
  return inputs.filter((input) => {
    const inputDescription = inputDescriptions.find(
      (desc) => desc.inputId === input.inputId,
    );

    if (!inputDescription || !isInputInFieldsGroup(inputDescription)) {
      return false;
    }

    return hasFieldValueOrClearOperator(input);
  }).length;
};

export const activateNextSection = (
  currActionHash: string,
  actions: ActionCandidate[],
  activateTrigger: () => void,
  activateNextAction: (actionHash: string) => void,
): void => {
  const currentIndex = actions.findIndex(
    (action) => action.hash === currActionHash,
  );

  const nextActionHash =
    actions[currentIndex - 1]?.hash ?? actions[currentIndex + 1]?.hash ?? "";

  if (nextActionHash) {
    activateNextAction(nextActionHash);
  } else {
    activateTrigger();
  }
};

export const showActionError = (
  selectedAction: ActionCandidate,
  actionsSubmitted: string[],
  triggerIds: string[],
  currentTriggerId?: string,
): boolean => {
  // for now we validate only send-gmail action
  if (selectedAction.actionReference?.actionId === "send-gmail") {
    const fileInputs = selectedAction.inputs.filter(
      (input) => input.inputId === "attachments-input",
    );

    return !!fileInputs.some((fileInput) => {
      if (fileInput.value?.values) {
        return validateAttachmentsInput(
          fileInput.value?.values,
          selectedAction.description as FileEditorDescription,
        );
      }

      return false;
    });
  }

  const hasError =
    selectedAction.deletedError ||
    selectedAction.accessError ||
    actionsSubmitted.includes(selectedAction.hash) ||
    (selectedAction.actionReference?.actionId === "merge-records" &&
      !triggerIds.includes(currentTriggerId ?? ""));

  return hasError;
};

export const showActionFieldMissingError = (
  isAllDynamicDescriptionsLoaded: boolean,
  candidateSelf: AutomationCandidate,
  selectedAction: ActionCandidate,
): boolean => {
  if (!isAllDynamicDescriptionsLoaded) {
    return false;
  }

  // Resolve currentItem/condition checks against the loop the selected
  // action actually lives in (falls back to the root loop for actions
  // outside any loop), instead of trusting a caller-passed root scope.
  const rootLooping = rootStore.automations.candidate.looping;
  const looping =
    rootLooping.scopeForAction(selectedAction.hash) ?? rootLooping;

  return (
    !candidateActionInputReferenceValid(candidateSelf, selectedAction) ||
    !candidateActionConditionsValid(candidateSelf, selectedAction, looping) ||
    !candidateActionInputValid(selectedAction)
  );
};

export const showActionValidationErrors = (
  isAllDynamicDescriptionsLoaded: boolean,
  candidate: CandidateStore,
  selectedAction: ActionCandidate,
  isSubmitted: boolean,
): boolean => {
  return (
    showActionFieldsEmptyError(selectedAction, isSubmitted) ||
    showActionFieldMissingError(
      isAllDynamicDescriptionsLoaded,
      candidate.self,
      selectedAction,
    )
  );
};

/**
 * Whether action input editors should show validation styling/messages.
 * Matches sidebar configuration-error visibility for non-new actions.
 */
export const shouldShowActionInputValidationErrors = (
  candidate: CandidateStore,
  action: ActionCandidate,
  isAllDynamicDescriptionsLoaded: boolean,
): boolean => {
  if (candidate.isActionSubmitted(action.hash)) {
    return true;
  }

  if (
    candidate.self.systemStatus !== undefined &&
    candidate.self.systemStatus.errors.length > 0
  ) {
    return true;
  }

  if (candidate.isNewlyAddedAction(action)) {
    return false;
  }

  return showActionValidationErrors(
    isAllDynamicDescriptionsLoaded,
    candidate,
    action,
    false,
  );
};

export const showRecordListSourceError = (
  candidate: CandidateStore,
  action: ActionCandidate,
  actionGroupType: ActionGroupType,
): boolean => {
  if (recordSourceMissing(action)) {
    return true;
  }

  if (action.description.recordListInput === undefined) {
    return false;
  }

  const findRecordsActions = candidate.findActionsWithRecordsList(
    action,
    actionGroupType,
  );

  const actionOptions = findRecordsActionsToOptions(findRecordsActions);
  const triggerOption = triggerToOption(candidate.self.trigger, action);

  return actionOptions.length === 0 && triggerOption === undefined;
};

/**
 * Checks if a create-record or update-record action has no fields with values.
 * Returns true if the action should show a fields empty error indicator.
 * Only shows error if the action has been submitted (user tried to save).
 */
export const showActionFieldsEmptyError = (
  selectedAction: ActionCandidate,
  isSubmitted: boolean = false,
): boolean => {
  if (!isSubmitted) {
    return false;
  }

  const actionId = selectedAction.actionReference?.actionId;

  if (!isRecordAction(actionId)) {
    return false;
  }

  const fieldsWithValuesCount = countFieldsGroupWithValues(
    selectedAction.inputs,
    selectedAction.description.inputs,
  );

  // Return true if no fields have values (error condition)
  return fieldsWithValuesCount === 0;
};

export const handleActionChange = (
  store: RootStore,
  integrationId: string,
  actionId: string,
  actionGroupType: ActionGroupType,
  onChangeAction: (
    integrationId: string,
    actionId: string,
    actionGroupType: ActionGroupType,
  ) => void,
  onClickAction: () => void,
): void => {
  store.modal.openConfirmModal({
    type: "warning",
    title: t("confirm-modal.are-you-sure"),
    description: `${t("confirm-modal.want-change-action")} ${t(
      "confirm-modal.this-may-invalidate-action",
    )}. ${t("confirm-modal.cannot-undone-action")}.`,
    positiveButtonText: t("confirm-modal.yes-change-action"),
    negativeButtonText: t("confirm-modal.no-cancel"),
    onClose: () => {
      store.modal.closeConfirmModal();
    },
    onConfirm: () => {
      onChangeAction(integrationId, actionId, actionGroupType);
      onClickAction();
      store.modal.closeConfirmModal();
    },
  });
};
